import { useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, User, Wallet, ArrowDownToLine, ArrowUpFromLine, History, ReceiptText, Gift, Bell, Headphones, Settings, LogOut, Menu, Gamepad2 } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/brand";
import UserMenu from "@/components/UserMenu";
import Footer from "@/components/Footer";
import { NAV } from "@/components/PublicHeader";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  ["My Account", [["Overview", "/account", LayoutDashboard], ["My Profile", "/account/profile", User], ["Wallet", "/account/wallet", Wallet],
    ["Add Coins (UPI)", "/account/deposit", ArrowDownToLine], ["Withdraw Funds", "/account/withdraw", ArrowUpFromLine],
    ["Transaction History", "/account/transactions", History], ["Bet History", "/account/bets", ReceiptText],
    ["Bonuses & Promotions", "/account/bonuses", Gift], ["Golden Arcade", "/arcade", Gamepad2], ["Notifications", "/account/notifications", Bell]]],
  ["Support", [["Support", "/account/support", Headphones], ["Settings", "/account/settings", Settings]]],
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const SideNav = ({ onNavigate }) => {
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="flex h-full flex-col">
      {LINKS.map(([group, items]) => (
        <div key={group} className="mb-5 border-b border-cr-line pb-5 last:border-0">
          <p className="mb-2 px-3 text-xs uppercase tracking-wider text-cr-muted">{group}</p>
          {items.map(([l, to, Icon]) => (
            <NavLink key={l} to={to} end onClick={onNavigate} data-testid={`side-${slug(l)}`} className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
              <Icon className="h-5 w-5" />{l}
            </NavLink>
          ))}
          {group === "Support" && (
            <button data-testid="side-logout" onClick={async () => { await logout(); nav("/login"); }} className="side-link w-full text-cr-danger"><LogOut className="h-5 w-5" />Logout</button>
          )}
        </div>
      ))}
      <div className="mt-auto rounded-xl border border-cr-gold/30 bg-gradient-to-br from-cr-gold/15 to-transparent p-4">
        <p className="font-display font-bold text-cr-gold">Get Exclusive Rewards</p>
        <p className="mt-1 text-xs text-cr-ink/75">See current offers and their terms.</p>
        <Link to="/account/bonuses" onClick={onNavigate} data-testid="side-view-offers" className="btn-gold mt-3 px-3 py-1.5 text-xs">View Offers</Link>
      </div>
    </div>
  );
};

export default function AccountLayout() {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  if (user === null) return <div className="grid min-h-screen place-items-center text-cr-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "super_admin") return <Navigate to="/super-admin" replace />;
  return (
    <div className="min-h-screen bg-cr-bg">
      <header className="sticky top-0 z-40 border-b border-cr-line bg-[#080909]/90 backdrop-blur-xl">
        <div className="flex h-20 items-center gap-6 px-4 lg:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger data-testid="account-mobile-menu-btn" aria-label="Open account menu" className="p-1 lg:hidden"><Menu className="h-6 w-6" /></SheetTrigger>
            <SheetContent side="left" className="w-72 overflow-y-auto border-cr-line bg-cr-bg p-4 text-cr-ink">
              <SheetTitle className="sr-only">Account menu</SheetTitle>
              <Logo className="mb-6 h-12" />
              <SideNav onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <Logo className="h-14" />
          <nav className="hidden flex-1 items-center gap-7 xl:flex">
            <NavLink to="/" end className="nav-link normal-case">Home</NavLink>
            {NAV.map(([l, to]) => <NavLink key={l} to={to} end className="nav-link normal-case">{l}</NavLink>)}
          </nav>
          <div className="ml-auto"><UserMenu /></div>
        </div>
      </header>
      <div className="flex">
        <aside className="sticky top-20 hidden h-[calc(100vh-5rem)] w-64 shrink-0 overflow-y-auto border-r border-cr-line p-4 lg:block">
          <SideNav />
        </aside>
        <main className="min-w-0 flex-1 px-4 py-8 sm:px-8"><Outlet /></main>
      </div>
      <Footer />
    </div>
  );
}
